import { ApiService } from '../apiService'
import { UserSearchDto } from '@features/adventures/types/adventure-editor.types'
import type {
  AdminCheckInCandidateDto,
  AdminAwardCheckInRequest,
  AdminAwardCheckInResultDto,
  AdminRankedAwardCandidateDto,
  AdminAwardRankedRequest,
  AdminAwardRankedResultDto,
} from '../types/checkInAward'

// ====================
// TYPES
// ====================

export interface AdvancedSearchRequest {
  query: string
  interestTags: string[]
  lookingForTags: string[]
  page: number
  pageSize: number
  location?: string
  maxDistance?: number
  minAge?: number
  maxAge?: number
}

export interface AdvancedSearchResponse {
  users: UserSearchDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

// ====================
// API
// ====================

export const usersApi = {
  // Simple search users
  searchUsers: (query: string, currentUserId: string, limit: number = 10) =>
    ApiService.get<UserSearchDto[]>('/api/users/search', { searchterm: query, currentUserId, limit }),

  // Advanced search users
  advancedSearch: (request: AdvancedSearchRequest) =>
    ApiService.post<AdvancedSearchResponse>('/api/users/search/advanced', request),

  // Adventures a missed visit could be awarded against, annotated with whether an award would
  // actually earn anything for this user.
  //
  // The `/admin/` prefix is what routes a call to the Admin API: client.ts rewrites it to
  // admapi.hadv.quest/api/..., and every other path goes to the User API instead. These endpoints
  // live on AdminUsersController, whose route is "api/users" — so the path here is /admin/users/...
  // and not the /api/users/... it resolves to.
  getCheckInCandidates: (userId: string, search?: string, take = 20) =>
    ApiService.get<AdminCheckInCandidateDto[]>(
      `/admin/users/${userId}/check-in-candidates`,
      { search: search ?? '', take },
    ),

  // Award a completion for a visit the system never recorded.
  awardCheckIn: (userId: string, request: AdminAwardCheckInRequest) =>
    ApiService.post<AdminAwardCheckInResultDto>(`/admin/users/${userId}/check-in-awards`, request),

  getRankedAwardCandidates: (userId: string, search: string) =>
    ApiService.get<AdminRankedAwardCandidateDto[]>(`/admin/users/${userId}/ranked-award-candidates`, { search, take: 20 }),

  awardRanked: (userId: string, request: AdminAwardRankedRequest) =>
    ApiService.post<AdminAwardRankedResultDto>(`/admin/users/${userId}/ranked-awards`, request),
}
